import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Edit, Image, FileText, Download } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BrandManager = ({ isLightMode }) => {
  const [brands, setBrands] = useState([]);
  const [editingBrand, setEditingBrand] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Form State
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.get(`${BACKEND_URL}/api/brands`);
      setBrands(response.data);
    } catch (error) {
      toast.error("Failed to load brands");
    }
  };

  const handleEdit = (brand) => {
    setEditingBrand(brand);
    setFormData({ ...brand });
    setIsDialogOpen(true);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    setLoading(true);
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("trade_token");

    try {
      // Handle Upload if a file is selected
      let pdfUrl = formData.pdfUrl;
      const fileInput = document.getElementById("pdf-upload");
      
      if (fileInput && fileInput.files[0]) {
          const file = fileInput.files[0];
          const uploadData = new FormData();
          uploadData.append("file", file);
          
          const uploadRes = await axios.post(`${BACKEND_URL}/api/upload`, uploadData, {
              headers: { 
                  "Content-Type": "multipart/form-data",
                  Authorization: `Bearer ${token}` 
              }
          });
          // Use the relative URL returned by the server directly
          // This ensures portable links (e.g. /api/uploads/file.pdf)
          pdfUrl = uploadRes.data.url;
          
          // Update local state before saving to DB
          formData.pdfUrl = pdfUrl;
      }
      
      await axios.put(`${BACKEND_URL}/api/brands/${editingBrand.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Brand Updated");
      setIsDialogOpen(false);
      fetchBrands();
      setEditingBrand(null);
      setFormData({});
    } catch (error) {
      toast.error("Update Failed");
    } finally {
      setLoading(false);
    }
  };

  const inputClass = `rounded-none h-10 ${isLightMode ? 'bg-gray-50 border-gray-300 text-gray-900' : 'bg-black/50 border-white/10 text-white'}`;
  const labelClass = `text-xs uppercase font-bold mb-2 block ${isLightMode ? 'text-gray-500' : 'text-gray-400'}`;

  return (
    <div className={`rounded-sm border ${isLightMode ? 'bg-white border-gray-200' : 'bg-dreibach-card border-white/10'}`}>
      <div className="p-6 border-b border-white/10">
        <h2 className={`text-xl font-bold uppercase tracking-widest ${isLightMode ? 'text-gray-900' : 'text-white'}`}>Brand Manager</h2>
        <p className={`text-sm ${isLightMode ? 'text-gray-500' : 'text-gray-400'}`}>Update catalog links and brand imagery.</p>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className={isLightMode ? 'border-gray-200 hover:bg-gray-50' : 'border-white/10 hover:bg-white/5'}>
              <TableHead className="w-[100px]">Image</TableHead>
              <TableHead>Brand Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead className="text-right">Downloads</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {brands.map((brand) => (
              <TableRow key={brand.id} className={isLightMode ? 'border-gray-200 hover:bg-gray-50' : 'border-white/10 hover:bg-white/5'}>
                <TableCell>
                  <img src={brand.logo || brand.image} alt={brand.name} className="h-10 w-10 object-contain rounded-sm bg-gray-800 p-1" />
                </TableCell>
                <TableCell className={`font-bold ${isLightMode ? 'text-gray-900' : 'text-white'}`}>{brand.name}</TableCell>
                <TableCell className={isLightMode ? 'text-gray-600' : 'text-gray-400'}>{brand.category}</TableCell>
                <TableCell className="text-right">
                    <span className="flex items-center justify-end gap-1 text-xs font-mono text-dreibach-teal">
                        <Download size={12} /> {brand.download_count || 0}
                    </span>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="outline" size="sm" onClick={() => handleEdit(brand)} className="gap-2">
                    <Edit size={14} /> Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className={`${isLightMode ? 'bg-white text-gray-900' : 'bg-dreibach-card border-white/10 text-white'}`}>
          <DialogHeader>
            <DialogTitle>Edit Brand: {editingBrand?.name}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className={labelClass}>Tile Image URL</Label>
              <div className="flex gap-2">
                 <Input name="image" value={formData.image || ''} onChange={handleChange} className={inputClass} />
                 <Button size="icon" variant="outline" onClick={() => window.open(formData.image, '_blank')}><Image size={14} /></Button>
              </div>
            </div>

            <div>
              <Label className={labelClass}>Catalog PDF URL</Label>
              <div className="flex gap-2">
                 <Input name="pdfUrl" value={formData.pdfUrl || ''} onChange={handleChange} className={inputClass} />
                 <Button size="icon" variant="outline" onClick={() => window.open(formData.pdfUrl, '_blank')}><FileText size={14} /></Button>
              </div>
            </div>

            <div>
              <Label className={labelClass}>Upload New Catalogue (Optional)</Label>
              <Input 
                id="pdf-upload" 
                type="file" 
                accept=".pdf" 
                className={`${inputClass} pt-2`} 
              />
              <p className="text-[10px] text-gray-500 mt-1">Selecting a file will replace the current URL upon save.</p>
            </div>

            <div>
              <Label className={labelClass}>Logo URL</Label>
              <Input name="logo" value={formData.logo || ''} onChange={handleChange} className={inputClass} />
            </div>
            
            <div className="pt-4 flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleSave} disabled={loading} className="bg-dreibach-teal text-white">
                    {loading ? "Saving..." : "Save Changes"}
                </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BrandManager;
